import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export type SvgLinkRoutes = Record<string, string>;

export type SvgNavProps = {
  /** Map of element IDs inside the SVG -> route to navigate to */
  linkRoutes: SvgLinkRoutes;

  /**
   * The imported SVG React component (e.g. `import { ReactComponent as Bkg } from "./bkg.svg"`)
   * Must render an <svg> somewhere inside.
   */
  SvgComponent: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;

  /** Optional classnames to match your existing CSS */
  containerClassName?: string; // default: "page-class-bkg"
  svgClassName?: string; // default: "page-bkg-svg"

  /** Class added to each clickable SVG element */
  linkClassName?: string; // default: "svg-nav-link"

  /** Called before navigation happens */
  onBeforeNavigate?: (id: string, route: string, event: Event) => void;

  /** If true, uses navigate(route, { replace: true }) */
  replace?: boolean;
};

function SvgRouteNavigator({
  linkRoutes,
  SvgComponent,
  containerClassName = "page-class-bkg",
  svgClassName = "page-bkg-svg",
  linkClassName = "svg-nav-link",
  onBeforeNavigate,
  replace = false,
}: SvgNavProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Works even if the SVG component does NOT forward refs.
    const svg = container.querySelector("svg");
    if (!svg) return;

    const cleanups: Array<() => void> = [];

    Object.entries(linkRoutes).forEach(([id, route]) => {
      const el = svg.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
      if (!el) return;

      const handleClick = (e: Event) => {
        onBeforeNavigate?.(id, route, e);
        navigate(route, { replace });
      };

      // const handleKeyDown = (e: KeyboardEvent) => {
      //   // Enter / Space
      //   if (e.key === "Enter" || e.key === " ") {
      //     e.preventDefault();
      //     onBeforeNavigate?.(id, route, e);
      //     navigate(route, { replace });
      //   }
      // };

      el.classList.add(linkClassName);
      // basic a11y for non-<a> SVG groups/paths
      // if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
      // if (!el.hasAttribute("role")) el.setAttribute("role", "link");

      el.addEventListener("click", handleClick);
      // el.addEventListener("keydown", handleKeyDown);

      cleanups.push(() => {
        el.removeEventListener("click", handleClick);
        // el.removeEventListener("keydown", handleKeyDown);
        el.classList.remove(linkClassName);
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [navigate, linkRoutes, linkClassName, onBeforeNavigate, replace]);

  return (
    <div ref={containerRef} className={containerClassName}>
      <SvgComponent className={svgClassName} />
    </div>
  );
}

export default SvgRouteNavigator;